import { useEffect, useState } from 'react';
import type { ActionResult } from '@/features/sites/store';

interface SiteData {
  id: number;
  siteName: string;
  currentState: string;
  description: string;
  requiredChanges: string;
  priority?: number;
  latitude?: number;
  longitude?: number;
}

interface SiteChanges {
  siteName: string;
  priority: number;
  currentState: string;
  description: string;
  requiredChanges: string;
  additionalWorks: string;
  latitude: number;
  longitude: number;
}

interface UseSiteFormProps {
  currentSite: SiteData | null;
  onUpdateSite: (siteId: number, changes: SiteChanges) => Promise<ActionResult>;
}

export const useSiteForm = ({
  currentSite,
  onUpdateSite,
}: UseSiteFormProps) => {
  // Form state
  const [siteName, setSiteName] = useState<string>('');
  const [currentPriority, setCurrentPriority] = useState<number>(1);
  const [currentState, setCurrentState] = useState<string>('');
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  }>({ lat: 0, lng: 0 });
  const [latitudeInput, setLatitudeInput] = useState<string>('0');
  const [longitudeInput, setLongitudeInput] = useState<string>('0');
  const [currentDescription, setCurrentDescription] = useState<string>('');
  const [currentRequiredChanges, setCurrentRequiredChanges] =
    useState<string>('');
  const [siteOldName, setSiteOldName] = useState<string>('');

  // Update status tracking
  const [updateStatus, setUpdateStatus] = useState({
    siteName: { error: false, updating: false, updated: false },
    priority: { error: false, updating: false, updated: false },
    currentState: { error: false, updating: false, updated: false },
    coordinates: { error: false, updating: false, updated: false },
    description: { error: false, updating: false, updated: false },
    requiredChanges: { error: false, updating: false, updated: false },
  });

  // Helper function to update specific field status
  const updateFieldStatus = (
    field: keyof typeof updateStatus,
    status: Partial<{ error: boolean; updating: boolean; updated: boolean }>
  ) => {
    setUpdateStatus(prev => ({
      ...prev,
      [field]: { ...prev[field], ...status },
    }));
  };

  // Set form values when currentSite changes
  useEffect(() => {
    if (currentSite) {
      setSiteOldName(currentSite.siteName);
      setSiteName(currentSite.siteName);
      setCurrentState(currentSite.currentState);
      setCurrentDescription(currentSite.description);
      setCurrentRequiredChanges(currentSite.requiredChanges);
      setCurrentPriority(currentSite.priority || 1);

      if (currentSite.latitude && currentSite.longitude) {
        const lat = Number(currentSite.latitude);
        const lng = Number(currentSite.longitude);

        if (!isNaN(lat) && !isNaN(lng)) {
          const coords = { lat, lng };
          setCoordinates(coords);
          setLatitudeInput(lat.toString());
          setLongitudeInput(lng.toString());
        }
      }
    }
  }, [currentSite]);

  // Universal site update function
  const updateSiteField = async (
    updates: Partial<{
      siteName: string;
      priority: number;
      currentState: string;
      description: string;
      requiredChanges: string;
      coordinates: { lat: number; lng: number };
    }>
  ): Promise<boolean> => {
    if (!currentSite) return false;

    const siteData: SiteChanges = {
      siteName: updates.siteName ?? currentSite.siteName,
      description: updates.description ?? currentSite.description,
      currentState: updates.currentState ?? currentSite.currentState,
      requiredChanges: updates.requiredChanges ?? currentSite.requiredChanges,
      additionalWorks: (currentSite as any).additionalWorks || '',
      priority: updates.priority ?? (currentSite.priority || 1),
      latitude: updates.coordinates?.lat ?? (currentSite.latitude || 0),
      longitude: updates.coordinates?.lng ?? (currentSite.longitude || 0),
    };

    const result = await onUpdateSite(currentSite.id, siteData);
    return result.success;
  };

  return {
    // Form state
    siteName,
    setSiteName,
    currentPriority,
    setCurrentPriority,
    currentState,
    setCurrentState,
    coordinates,
    setCoordinates,
    latitudeInput,
    setLatitudeInput,
    longitudeInput,
    setLongitudeInput,
    currentDescription,
    setCurrentDescription,
    currentRequiredChanges,
    setCurrentRequiredChanges,
    siteOldName,

    // Update status
    updateStatus,
    updateFieldStatus,

    // Actions
    updateSiteField,
  };
};
